import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { McButton, McIcon, McInput, McTag } from '@maersk-global/mds-react-wrapper/components-core';
import queryService from '../../services/queryService';
import './TagFilterChips.css';

interface TagFilterChipsProps {
    selectedTags: string[];
    onTagsChange: (tags: string[]) => void;
    predefinedTags?: string[];
    applicationId?: string | null;
}

const DEFAULT_PREDEFINED_TAGS = ['Tech Support', 'Sentinel', 'Application'];

export const TagFilterChips: React.FC<TagFilterChipsProps> = ({
    selectedTags,
    onTagsChange,
    predefinedTags = DEFAULT_PREDEFINED_TAGS,
    applicationId,
}) => {
    const [customTag, setCustomTag] = useState('');

    // Fetch all available tags
    const { data: allTags = [] } = useQuery({
        queryKey: ['tags', applicationId || 'all'],
        queryFn: () => queryService.getAllTags(applicationId || undefined),
    });

    const handleTagToggle = (tag: string) => {
        if (selectedTags.includes(tag)) {
            onTagsChange(selectedTags.filter((t) => t !== tag));
        } else {
            onTagsChange([...selectedTags, tag]);
        }
    };

    const handleAddCustomTag = () => {
        if (customTag.trim() && !selectedTags.includes(customTag.trim())) {
            onTagsChange([...selectedTags, customTag.trim()]);
            setCustomTag('');
        }
    };

    const handleClearAll = () => {
        onTagsChange([]);
    };

    // Combine predefined and existing tags
    const suggestedTags = Array.from(
        new Set([...predefinedTags, ...allTags])
    ).sort();

    return (
        <div className="tag-filter-chips">
            <div className="tag-filter-header">
                <McIcon icon="tag" />
                <span className="tag-filter-label">Filter by Tags</span>
                {selectedTags.length > 0 && (
                    <McButton
                        variant="plain"
                        appearance="neutral"
                        click={handleClearAll}
                        className="clear-tags-button"
                    >
                        Clear all
                    </McButton>
                )}
            </div>

            <div className="tag-chips-container">
                {suggestedTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    const isPredefined = predefinedTags.includes(tag);

                    return (
                        <McTag
                            key={tag}
                            appearance={
                                isPredefined
                                    ? tag === 'Tech Support'
                                        ? 'info'
                                        : tag === 'Sentinel'
                                            ? 'success'
                                            : 'warning'
                                    : 'neutral'
                            }
                            onClick={() => handleTagToggle(tag)}
                            className={`tag-chip ${isSelected ? 'selected' : ''}`}
                        >
                            {tag}
                        </McTag>
                    );
                })}
            </div>

            <div className="custom-tag-input">
                <McInput
                    label="Add custom tag"
                    hiddenlabel
                    placeholder="Type a custom tag..."
                    value={customTag}
                    input={(e: any) => setCustomTag(e.target.value)}
                    onKeyPress={(e: any) => {
                        if (e.key === 'Enter') {
                            handleAddCustomTag();
                        }
                    }}
                    icon="tag"
                />
                <McButton
                    variant="outlined"
                    appearance="neutral"
                    click={handleAddCustomTag}
                    disabled={!customTag.trim()}
                >
                    Add
                </McButton>
            </div>

            {selectedTags.length > 0 && (
                <div className="selected-tags-summary">
                    <span className="summary-label">Selected:</span>
                    <div className="selected-tags-list">
                        {selectedTags.map((tag) => (
                            <McTag
                                key={tag}
                                appearance="neutral"
                                onClick={() => handleTagToggle(tag)}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                {tag} <McIcon icon="times" style={{ marginLeft: '4px' }} />
                            </McTag>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TagFilterChips;
